/**
 * NMS Care Guide Component
 * Modal-based search for product care guide
 */

class NMSCareGuide extends window.NMSBaseClass {
  constructor(element) {
    super();
    this.element = element;
    this.sectionId = element.dataset.sectionId;
    this.defaultSearch = element.dataset.defaultSearch || '';
    this.maxSuggestions = parseInt(element.dataset.maxSuggestions) || 3;

    window.neemans.careGuide = window.neemans.careGuide || {};
    window.neemans.careGuide[this.sectionId] = this;

    this.searchTerm = '';
    this.allItems = [];
    this.popularItems = [];
    this.visibleCount = 0;
    this.isModalOpen = false;
    this.selectedProduct = null;

    this.debouncedSearch = this.debounce(this.performSearch.bind(this), 300);
  }

  init() {
    super.init();
    this.#setupCareItems();
    this.#setupPopularSuggestions();
    this.#bindEvents();
  }

  #setupCareItems() {
    if (!this.refs.modal) return;

    const careItems = this.refs.modal.querySelectorAll('[data-care-item]');
    this.allItems = Array.from(careItems).map(item => ({
      element: item,
      searchText: item.dataset.searchText || '',
      displayName: item.dataset.displayName || '',
      desktopImage: item.dataset.desktopImage || '',
      mobileImage: item.dataset.mobileImage || ''
    }));
  }

  #setupPopularSuggestions(filterKeyword = '') {
    if (!this.refs.suggestionsList || this.allItems.length === 0) return;

    if (filterKeyword) {
      const keyword = filterKeyword.toLowerCase();
      this.popularItems = this.allItems
        .filter(item => item.searchText.includes(keyword))
        .slice(0, this.maxSuggestions);
    } else {
      this.popularItems = this.allItems.slice(0, this.maxSuggestions);
    }

    this.#renderPopularSuggestions();
  }

  #renderPopularSuggestions() {
    if (!this.refs.suggestionsList) return;

    const html = this.popularItems.map(item => `
      <button
        type="button"
        class="care-result-item"
        data-suggestion-item
        data-display-name="${this.#escapeHtml(item.displayName)}"
        data-desktop-image="${item.desktopImage}"
        data-mobile-image="${item.mobileImage}"
      >
        <svg class="care-result-item__icon" width="21" height="20" viewBox="0 0 21 20" fill="none">
          <path d="M9.16406 14.8809C12.6158 14.8809 15.4141 12.0826 15.4141 8.63086C15.4141 5.17908 12.6158 2.38086 9.16406 2.38086C5.71228 2.38086 2.91406 5.17908 2.91406 8.63086C2.91406 12.0826 5.71228 14.8809 9.16406 14.8809Z" stroke="#3A3A3A" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M13.5859 13.0508L17.9164 17.3812" stroke="#3A3A3A" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="care-result-item__name nms-text-label-md">${this.#escapeHtml(item.displayName)}</span>
        <svg class="care-result-item__arrow" width="17" height="16" viewBox="0 0 17 16" fill="none">
          <path d="M6.5 3L11.5 8L6.5 13" stroke="#3A3A3A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `).join('');

    this.refs.suggestionsList.innerHTML = html;

    const suggestionItems = this.refs.suggestionsList.querySelectorAll('[data-suggestion-item]');
    for (const item of suggestionItems) {
      item.addEventListener('click', () => this.#handleProductSelect(item));
    }
  }

  #bindEvents() {
    if (this.refs.triggerButton) {
      this.refs.triggerButton.addEventListener('click', () => this.openModal());
    }

    if (this.refs.clearButton) {
      this.refs.clearButton.addEventListener('click', () => this.clearSelection());
    }

    if (this.refs.triggerInput) {
      this.refs.triggerInput.addEventListener('click', () => this.openModal());
      this.refs.triggerInput.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.openModal();
        }
      });
    }

    if (this.refs.closeButton) {
      this.refs.closeButton.addEventListener('click', () => this.closeModal());
    }

    if (this.refs.modal) {
      this.refs.modal.addEventListener('click', event => {
        if (event.target === this.refs.modal) {
          this.closeModal();
        }
      });
    }

    if (this.refs.searchInput) {
      this.refs.searchInput.addEventListener('input', event => {
        this.handleSearchInput(event);
        this.#updatePlaceholderVisibility();
      });

      this.refs.searchInput.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          this.closeModal();
        }
      });
    }

    const resultItems = this.element.querySelectorAll('[data-care-item]');
    for (const item of resultItems) {
      item.addEventListener('click', () => this.#handleProductSelect(item));
    }
  }

  openModal() {
    if (!this.refs.modal || this.isModalOpen) return;

    this.refs.modal.showModal();
    this.isModalOpen = true;

    if (this.defaultSearch) {
      this.#setupPopularSuggestions(this.defaultSearch);
    }

    this.#showSuggestionsState();
    this.#updatePlaceholderVisibility();

    if (this.refs.searchInput) {
      setTimeout(() => this.refs.searchInput.focus(), 100);
    }

    document.body.style.overflow = 'hidden';
    this.dispatchNMSEvent('care-guide:modal-opened');
  }

  closeModal() {
    if (!this.refs.modal || !this.isModalOpen) return;

    this.refs.modal.close();
    this.isModalOpen = false;

    this.clearSearch();

    if (this.defaultSearch) {
      this.#setupPopularSuggestions(this.defaultSearch);
    } else {
      this.#setupPopularSuggestions();
    }

    document.body.style.overflow = '';
    this.dispatchNMSEvent('care-guide:modal-closed');
  }

  handleSearchInput(event) {
    this.searchTerm = event.target.value.trim().toLowerCase();
    this.debouncedSearch();
  }

  performSearch() {
    if (!this.searchTerm) {
      this.#showSuggestionsState();
      return;
    }

    this.#showResultsState();
    this.visibleCount = 0;

    for (const item of this.allItems) {
      const matches = item.searchText.includes(this.searchTerm);
      item.element.hidden = !matches;

      if (matches) {
        this.visibleCount++;
      }
    }

    if (this.visibleCount === 0) {
      this.#showNoResultsState();
    }

    this.dispatchNMSEvent('care-guide:search', {
      searchTerm: this.searchTerm,
      resultCount: this.visibleCount
    });
  }

  clearSearch() {
    if (this.refs.searchInput) {
      this.refs.searchInput.value = '';
    }

    this.searchTerm = '';
    this.#updatePlaceholderVisibility();
    this.#showSuggestionsState();
  }

  #updatePlaceholderVisibility() {
    if (!this.refs.searchPlaceholder || !this.refs.searchInput) return;

    const hasValue = this.refs.searchInput.value.trim().length > 0;
    this.refs.searchPlaceholder.hidden = hasValue;
  }

  #showSuggestionsState() {
    this.#toggleContainers(true, false, false);
  }

  #showResultsState() {
    this.#toggleContainers(false, true, false);
  }

  #showNoResultsState() {
    this.#toggleContainers(false, false, true);

    const title = this.refs.noResults?.querySelector('.care-no-results__title');
    if (title && this.searchTerm) {
      title.innerHTML = `No results found for <span>"${this.#escapeHtml(this.searchTerm)}"</span>`;
    }
  }

  #toggleContainers(showSuggestions, showResults, showNoResults) {
    if (this.refs.suggestionsContainer) {
      this.refs.suggestionsContainer.hidden = !showSuggestions;
    }
    if (this.refs.resultsContainer) {
      this.refs.resultsContainer.hidden = !showResults;
    }
    if (this.refs.noResults) {
      this.refs.noResults.hidden = !showNoResults;
    }
  }

  #handleProductSelect(element) {
    const productData = {
      displayName: element.dataset.displayName,
      desktopImage: element.dataset.desktopImage,
      mobileImage: element.dataset.mobileImage
    };

    this.selectedProduct = productData;
    this.#showCareContent(productData);
    this.closeModal();
    this.dispatchNMSEvent('care-guide:product-selected', productData);
  }

  #showCareContent(productData) {
    if (this.refs.emptyStateSection) {
      this.refs.emptyStateSection.hidden = true;
    }

    if (this.refs.careContent) {
      this.refs.careContent.hidden = false;
    }

    this.#updateTriggerInputText(productData.displayName);
    this.#toggleButtons(true);
    this.#setTriggerFieldActive(true);

    if (this.refs.desktopImageSource) {
      this.refs.desktopImageSource.srcset = productData.desktopImage;
    }

    if (this.refs.mobileImage) {
      this.refs.mobileImage.src = productData.mobileImage;
      this.refs.mobileImage.alt = `Care guide for ${productData.displayName}`;
    }

    if (this.refs.careContent) {
      this.refs.careContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  #updateTriggerInputText(productName) {
    const textSpan = this.refs.triggerInput?.querySelector('.nms-care-guide__trigger-text');
    if (textSpan) {
      textSpan.textContent = productName;
    }
  }

  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  clearSelection() {
    if (this.refs.careContent) {
      this.refs.careContent.hidden = true;
    }

    if (this.refs.emptyStateSection) {
      this.refs.emptyStateSection.hidden = false;
    }

    this.#resetTriggerInputText();
    this.#toggleButtons(false);
    this.#setTriggerFieldActive(false);
    this.selectedProduct = null;
    this.dispatchNMSEvent('care-guide:selection-cleared');
  }

  #resetTriggerInputText() {
    const textSpan = this.refs.triggerInput?.querySelector('.nms-care-guide__trigger-text');
    if (!textSpan) return;

    if (this.defaultSearch) {
      textSpan.innerHTML = `Search <span data-trigger-keyword>${this.#escapeHtml(this.defaultSearch)}</span>`;
    } else {
      textSpan.textContent = 'Search';
    }
  }

  #toggleButtons(showClear) {
    if (this.refs.triggerButton) {
      this.refs.triggerButton.hidden = showClear;
    }
    if (this.refs.clearButton) {
      this.refs.clearButton.hidden = !showClear;
    }
  }

  #setTriggerFieldActive(isActive) {
    const textSpan = this.refs.triggerInput?.querySelector('.nms-care-guide__trigger-text');
    if (!textSpan) return;

    if (isActive) {
      textSpan.classList.add('nms-care-guide__trigger-text--active');
    } else {
      textSpan.classList.remove('nms-care-guide__trigger-text--active');
    }
  }

  destroy() {
    super.destroy();

    if (this.isModalOpen) {
      this.closeModal();
    }

    if (window.neemans.careGuide?.[this.sectionId]) {
      delete window.neemans.careGuide[this.sectionId];
    }
  }
}

function initNMSCareGuide() {
  if (!window.NMSBaseClass) {
    console.error('NMSBaseClass required for NMSCareGuide');
    return;
  }

  const elements = document.querySelectorAll('.nms-care-guide');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      const instance = new NMSCareGuide(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSCareGuide);
} else {
  initNMSCareGuide();
}

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', event => {
    if (event.target.querySelector('.nms-care-guide')) {
      initNMSCareGuide();
    }
  });
}
